import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Popup, Input } from 'components/UIElements'
import { Link } from 'react-router-dom'
import { MIN_QUESTIONS_FOR_SHUFFLE } from 'utils/constants'

const SurveyToolbar = props => {
  const {
    consent,
    downloadConsentPreview,
    errors,
    id,
    isConsent,
    isConsentTwo,
    isLoading,
    onRandomize,
    onSave,
    onToggleTranslationPanel,
    studyID,
    showTranslationPanel,
    title,
    type,
    updateTitle,
    version,
    wordCount,
    questions = {},
    isReadOnlyState,
    studyVersionID,
    reviewScreen,
    toggleAllItemAttribute,
    updateMetadataField,
  } = props

  const [isReviewEnabled, setReviewEnabled] = useState(() => reviewScreen !== false)

  const updateReviewScreen = () => {
    updateMetadataField('review_screen', { enabled: !isReviewEnabled })
    setReviewEnabled(!isReviewEnabled)
    toggleAllItemAttribute({ attribute: 'in_review', value: !isReviewEnabled })
  }

  const previewURL =
    id === 'consent'
      ? `/studies/${studyID}/${studyVersionID}/e-consent/consent/preview`
      : `/studies/${studyID}/${studyVersionID}/instruments/${id}/preview`
  const isSurvey = type === 'SURVEY'
  const isArticle = type === 'ARTICLE'

  // We need to disabled shuffling if survey contains only 1 question and intro or just an intro
  const isShuffleDisabled = Object.keys(questions)?.length <= MIN_QUESTIONS_FOR_SHUFFLE

  return (
    <div className='survey-toolbar_placeholder'>
      <div className='spacer' />
      <div className='survey-toolbar'>
        {(isSurvey || isArticle) && !isLoading && (
          <Input
            id='survey-name-input'
            className='survey-title-input'
            hasError={title === '' && !!errors.titleErr}
            placeholder={errors.titleErr || `Enter your ${isSurvey ? 'survey' : 'article'} name here`}
            onChange={value => updateTitle(value)}
            value={title}
            spellCheck
            disabled={isReadOnlyState}
          />
        )}
        {isConsent && (
          <div className='flexed column start-aligned consent-title-wrapper'>
            <div className='consent-title' title={isConsentTwo ? consent?.consent_title : 'Consent'}>
              {isConsentTwo ? consent?.consent_title : 'Consent'}
            </div>
            {isConsentTwo && (
              <div className='label-small consent-language-toolbar flexed start-aligned'>
                <span>Default Language: en_US</span>
                {onToggleTranslationPanel && (
                  <button
                    type='button'
                    className='translation-panel-toggle'
                    onClick={onToggleTranslationPanel}>
                    {showTranslationPanel ? 'Hide languages' : 'Manage languages'}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        {!isArticle && !isConsentTwo && (
          <div className='button-list'>
            {id && (
              <Link className='clickable-wrapper clickable toolbar-button' to={previewURL} target='_blank'>
                <i className='fa fa-eye' />
                <span>Preview</span>
              </Link>
            )}
            {(id === 'consent' || isConsent) && (
              <div className='clickable toolbar-button' onClick={() => downloadConsentPreview(consent, studyID)}>
                <i className='fas fa-cloud-download-alt' />
                <span>Export to PDF</span>
              </div>
            )}
            {version && <VersionDropdown version={version} previewURL={previewURL} />}
            {isSurvey && onRandomize && (
              <div className={`clickable toolbar-button ${isShuffleDisabled ? 'disabled' : ''}`} onClick={onRandomize}>
                <i id='shuffle-questions' className='fas fa-random' />
                <span>Shuffle Questions</span>
              </div>
            )}
            {isSurvey && (
              <Popup
                hover
                noPointer
                dark
                position='right'
                trigger={
                  <div className={`clickable toolbar-button`} onClick={updateReviewScreen}>
                    <i className={`fa fa-eye${isReviewEnabled ? '' : '-slash'}`} />
                    <span>Review Screen {isReviewEnabled ? 'Enabled' : 'Disabled'}</span>
                  </div>
                }>
                <span className='popup-message'>Instrument review screen can be enabled or disabled</span>
              </Popup>
            )}
            <div className='word-count' id='word-count'>
              <span>{`Word Count: ${wordCount}`}</span>
            </div>
          </div>
        )}
      </div>
      <div className='spacer' />
    </div>
  )
}

const VersionDropdown = ({ version, previewURL }) => {
  const versions = [<h6 key='head'>Preview Version:</h6>]
  for (let v = 1; v < version; v++) {
    versions.push(
      <Link key={v} className='clickable-wrapper' to={`${previewURL}?version=${v}`} target='_blank'>
        <p className='clickable'>{v}</p>
      </Link>,
    )
  }
  return (
    <Popup
      align='left'
      disabled={versions.length < 2}
      className='version-dropdown clickable'
      trigger={
        <div className='toolbar-button'>
          <i className='fas fa-history' />
          <span>Previous Versions</span>
        </div>
      }>
      {versions}
    </Popup>
  )
}

VersionDropdown.propTypes = {
  previewURL: PropTypes.string,
  version: PropTypes.number,
}

SurveyToolbar.propTypes = {
  consent: PropTypes.shape({
    id: PropTypes.string,
  }),
  downloadConsentPreview: PropTypes.func,
  errors: PropTypes.shape({
    titleErr: PropTypes.string,
  }),
  isConsent: PropTypes.bool,
  isConsentTwo: PropTypes.bool,
  isLoading: PropTypes.bool,
  onRandomize: PropTypes.func,
  onSave: PropTypes.func,
  onToggleTranslationPanel: PropTypes.func,
  showTranslationPanel: PropTypes.bool,
  studyID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  title: PropTypes.string,
  type: PropTypes.string,
  updateTitle: PropTypes.func,
  version: PropTypes.number,
  wordCount: PropTypes.number,
  id: PropTypes.string,
}

export default SurveyToolbar
